import { useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { format } from "date-fns";
import { toast } from "sonner";
import { IGIC_RATE, LOCATION_FISCAL_DATA } from "@/constants/fiscal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ExportMenuProps {
  token: string;
  isSuperAdmin: boolean;
  selectedLocationId: string | null;
  locations?: Array<{ id: string; name: string; code?: string }>;
}

// Mapa hardcodeado de UUIDs reales a códigos de sede (fallback)
const LOCATION_UUID_TO_CODE: Record<string, string> = {};

export const ExportMenu = ({ token, isSuperAdmin, selectedLocationId, locations = [] }: ExportMenuProps) => {
  const [isExporting, setIsExporting] = useState(false);

  // Construir mapa dinámico de UUID -> código cuando locations cambie
  const locationIdToCode = locations.reduce((acc, loc) => {
    if (loc.code) acc[loc.id] = loc.code;
    return acc;
  }, { ...LOCATION_UUID_TO_CODE } as Record<string, string>);

  // Helper robusto para obtener nombre de sede
  const getLocationName = (locationId: string | null): string => {
    if (!locationId) {
      // Si no hay ID, usamos fallback para empleados
      return isSuperAdmin ? "Desconocida" : "Sede Actual";
    }
    
    // 1. Buscar directamente en el array de locations por ID
    const locFromProps = locations.find(l => l.id === locationId);
    if (locFromProps?.name) {
      return locFromProps.name;
    }
    
    // 2. Buscar código en el mapa y luego en datos fiscales
    const code = locationIdToCode[locationId];
    if (code && LOCATION_FISCAL_DATA[code]) {
      return LOCATION_FISCAL_DATA[code].name.replace("Pastelería Reyes - ", "");
    }
    
    // 3. Fallback sensato
    return isSuperAdmin ? "Desconocida" : "Sede Actual";
  };

  const exportToExcel = async (type: 'orders' | 'encargos') => {
    try {
      setIsExporting(true);
      toast.info("Generando informe...");

      // Usar Edge Function para obtener pedidos (respeta permisos)
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "fetch-history",
          token,
          locationId: isSuperAdmin ? selectedLocationId : undefined, // Solo superadmin puede filtrar
        },
      });

      if (error) throw error;
      if (!data?.success) {
        toast.error(data?.error || "Error al obtener datos");
        setIsExporting(false);
        return;
      }

      let orders = data.orders || [];

      // Filtrar por tipo de pedido
      if (type === 'encargos') {
        orders = orders.filter((o: any) => o.order_type === 'encargo' && o.payment_status === 'pagado');
      } else {
        orders = orders.filter((o: any) => o.order_type !== 'encargo' && o.status === 'completado');
      }

      if (orders.length === 0) {
        toast.warning("No hay datos para exportar en este periodo.");
        setIsExporting(false);
        return;
      }

      // Formatear Datos para Excel
      const excelData = orders.map((order: any) => {
        const total = order.total || 0;
        const base = total / (1 + IGIC_RATE);
        const igic = total - base;
        const dateObj = new Date(order.created_at);

        // Usar ticket_series y series_number si existen
        let prettyId: string;
        if (order.ticket_series && order.series_number) {
          prettyId = `${order.ticket_series}-${dateObj.getFullYear()}-${String(order.series_number).padStart(5, '0')}`;
        } else {
          const prefix = order.order_type === 'mesa' ? 'M' : order.order_type === 'encargo' ? 'E' : 'L';
          prettyId = `${prefix}-${dateObj.getFullYear()}-${String(order.order_number).padStart(4, '0')}`;
        }

        // Formatear tipo de pedido
        const tipoMap: Record<string, string> = {
          'mesa': 'Mesa',
          'llevar': 'Para Llevar',
          'encargo': 'Encargo'
        };

        // Formatear método de pago
        const pagoMap: Record<string, string> = {
          'online': 'Tarjeta Online',
          'local': 'TPV/Efectivo',
          'efectivo': 'Efectivo',
          'tarjeta': 'Tarjeta TPV'
        };

        // Resolver nombre de sede
        const locationName = getLocationName(order.location_id);

        return {
          "Nº Factura": prettyId,
          "Fecha": format(dateObj, "dd/MM/yyyy"),
          "Hora": format(dateObj, "HH:mm"),
          "Sede": locationName,
          "Tipo": tipoMap[order.order_type] || order.order_type,
          "Ubicación": order.table_number ? `Mesa ${order.table_number}` : "Barra/Mostrador",
          "Atendido Por": order.served_by || "Web",
          "Cliente": order.customer_name || "-",
          "Método Pago": pagoMap[order.payment_method] || order.payment_method || "Efectivo",
          "Base Imponible": Number(base.toFixed(2)),
          "IGIC (7%)": Number(igic.toFixed(2)),
          "TOTAL": Number(total.toFixed(2))
        };
      });

      // Añadir fila de totales al final
      const totalBase = excelData.reduce((sum: number, row: any) => sum + row["Base Imponible"], 0);
      const totalIgic = excelData.reduce((sum: number, row: any) => sum + row["IGIC (7%)"], 0);
      const totalTotal = excelData.reduce((sum: number, row: any) => sum + row["TOTAL"], 0);

      excelData.push({
        "Nº Factura": "",
        "Fecha": "",
        "Hora": "",
        "Sede": "",
        "Tipo": "",
        "Ubicación": "",
        "Atendido Por": "",
        "Cliente": "",
        "Método Pago": "TOTALES:",
        "Base Imponible": Number(totalBase.toFixed(2)),
        "IGIC (7%)": Number(totalIgic.toFixed(2)),
        "TOTAL": Number(totalTotal.toFixed(2))
      });

      // Generar Libro Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Ajustar ancho de columnas
      const wscols = [
        { wch: 18 }, // Nº Factura
        { wch: 12 }, // Fecha
        { wch: 8 },  // Hora
        { wch: 18 }, // Sede
        { wch: 12 }, // Tipo
        { wch: 15 }, // Ubicación
        { wch: 15 }, // Atendido
        { wch: 20 }, // Cliente
        { wch: 15 }, // Pago
        { wch: 14 }, // Base
        { wch: 12 }, // IGIC
        { wch: 12 }, // Total
      ];
      worksheet['!cols'] = wscols;

      const workbook = XLSX.utils.book_new();
      const sheetName = type === 'encargos' ? "Encargos" : "Ventas";
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Descargar
      const fileName = `Informe_${type === 'encargos' ? 'Encargos' : 'Ventas'}_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" 
      });
      saveAs(blob, fileName);

      toast.success(`✅ Excel descargado: ${orders.length} registros`);

    } catch (error) {
      console.error("Error exportando:", error);
      toast.error("Error al generar el Excel");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800"
          disabled={isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4" />
          )}
          Exportar Excel
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-background">
        <DropdownMenuLabel>Selecciona Informe</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={() => exportToExcel('orders')} 
          className="cursor-pointer"
          disabled={isExporting}
        >
          <Download className="mr-2 h-4 w-4" /> 
          📊 Ventas Mesa/Barra
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => exportToExcel('encargos')} 
          className="cursor-pointer"
          disabled={isExporting}
        >
          <Download className="mr-2 h-4 w-4" /> 
          📦 Listado Encargos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
